import React, {useCallback, useEffect, useRef, useState} from "react"
import {
	CI_BRANCHES,
	CiBranch,
	CiBuildStatus,
	CiTarget,
	isCiBuildActive,
	useDevicesStore,
} from "../../store/devicesStore"

const POLL_INTERVAL_MS = 10000
const TARGETS: CiTarget[] = ["angel", "falcon"]
const TARGET_LABEL: Record<string, string> = {angel: "Angel", falcon: "Falcon"}

type PerTarget<T> = Record<CiTarget, T>

const isCiTarget = (value: unknown): value is CiTarget => TARGETS.includes(value as CiTarget)

/** Sets one target's entry, keeping the object identity when nothing changes. */
const setFor = <T,>(setter: React.Dispatch<React.SetStateAction<PerTarget<T>>>, target: CiTarget, value: T) =>
	setter((prev) => (prev[target] === value ? prev : {...prev, [target]: value}))

const formatElapsed = (ms: number): string => {
	if (!Number.isFinite(ms) || ms < 0) return "-"
	const total = Math.floor(ms / 1000)
	const h = Math.floor(total / 3600)
	const m = Math.floor((total % 3600) / 60)
	const s = total % 60
	const mm = m.toString().padStart(2, "0")
	const ss = s.toString().padStart(2, "0")
	return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`
}

const parseTime = (iso: string | null): number | null => {
	if (!iso) return null
	const t = Date.parse(iso)
	return Number.isNaN(t) ? null : t
}

interface BuildCardProps {
	target: CiTarget
	build: CiBuildStatus | null
	loaded: boolean
	now: number
	pollError: string | null
	showOutput: boolean
	onToggleOutput: () => void
}

/** Latest console build of one target (Angel / Falcon). */
const BuildCard: React.FC<BuildCardProps> = ({target, build, loaded, now, pollError, showOutput, onToggleOutput}) => {
	const label = TARGET_LABEL[target]

	if (!build) {
		return (
			<div className="p-3 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">
				<span className="text-gray-500 dark:text-gray-400">Build:</span>{" "}
				<span className="font-semibold text-gray-700 dark:text-gray-200">{label}</span>
				<span className="ml-4">
					{pollError
						? `Could not load the latest build status: ${pollError}`
						: loaded
							? "No console build in the last 30 days"
							: "Loading..."}
				</span>
			</div>
		)
	}

	const active = isCiBuildActive(build.status)
	const success = build.status === "Success"
	const requestedAt = parseTime(build.requestedAt)
	const finishedAt = parseTime(build.finishedAt)
	const elapsed = requestedAt ? (finishedAt ?? (active ? now : requestedAt)) - requestedAt : NaN

	const statusClass = active
		? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700"
		: success
			? "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700"
			: "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700"

	return (
		<div className="p-3 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm">
			<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-700 dark:text-gray-200">
				<span>
					<span className="text-gray-500 dark:text-gray-400">Build:</span>{" "}
					<span className="font-semibold">{label}</span>
				</span>
				<span>
					<span className="text-gray-500 dark:text-gray-400">Branch:</span>{" "}
					<span className="font-semibold">{build.branch || "?"}</span>
				</span>
				<span>
					<span className="text-gray-500 dark:text-gray-400">{active ? "Running for" : "Duration"}:</span>{" "}
					<span className="font-mono">{formatElapsed(elapsed)}</span>
				</span>
				{requestedAt && (
					<span className="text-gray-500 dark:text-gray-400">
						started {new Date(requestedAt).toLocaleString()}
					</span>
				)}
				<span className={`px-2 py-0.5 rounded border text-xs font-semibold ${statusClass}`}>
					{active && <span className="inline-block w-2 h-2 mr-1 rounded-full bg-amber-500 animate-pulse align-middle" />}
					{build.status}
				</span>
			</div>
			{active && (
				<div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
					A build takes about 10-12 minutes. Status is refreshed every 10 s.
				</div>
			)}
			{pollError && (
				<div className="mt-2 text-xs text-red-600 dark:text-red-300">
					Status refresh failed: {pollError}
					{active ? " (retrying)" : ""}
				</div>
			)}
			<button onClick={onToggleOutput} className="mt-2 text-xs text-blue-600 dark:text-blue-300 hover:underline">
				{showOutput ? "Hide output" : "Show output"}
			</button>
			{showOutput && (
				<pre className="mt-2 max-h-80 overflow-auto p-2 rounded bg-gray-900 text-gray-100 dark:bg-black text-xs font-mono whitespace-pre-wrap break-all">
					{build.outputTail || "(no output yet)"}
				</pre>
			)}
		</div>
	)
}

/**
 * CI build trigger (admin / developer): "Make Angel" / "Make Falcon" start master.sh / release.sh on
 * the CI host through the firmware Lambda (SSM). Shows the latest console build of each target
 * (Angel and Falcon separately) and polls a build while it runs; a running build is picked up again
 * after a page refresh. The CI host runs one console build at a time, so both buttons are disabled
 * while any build runs.
 */
const CiBuildSection: React.FC = () => {
	const startCiBuild = useDevicesStore((s) => s.startCiBuild)
	const getCiBuildStatus = useDevicesStore((s) => s.getCiBuildStatus)
	const markCiBuildFinished = useDevicesStore((s) => s.markCiBuildFinished)

	const [builds, setBuilds] = useState<PerTarget<CiBuildStatus | null>>({angel: null, falcon: null})
	const [loaded, setLoaded] = useState<PerTarget<boolean>>({angel: false, falcon: false})
	const [pollErrors, setPollErrors] = useState<PerTarget<string | null>>({angel: null, falcon: null})
	const [showOutput, setShowOutput] = useState<PerTarget<boolean>>({angel: false, falcon: false})
	const [chooserTarget, setChooserTarget] = useState<CiTarget | null>(null)
	const [branch, setBranch] = useState<CiBranch>("master")
	const [starting, setStarting] = useState(false)
	const [message, setMessage] = useState<{kind: "error" | "info"; text: string} | null>(null)
	const [now, setNow] = useState(() => Date.now())

	const mounted = useRef(true)
	// Status of the previous poll per target, to notice the transition running -> finished.
	const lastStatus = useRef<PerTarget<string | null>>({angel: null, falcon: null})
	// Newest request per target wins (an older, slower response must not overwrite a newer build).
	const requestSeq = useRef<PerTarget<number>>({angel: 0, falcon: 0})

	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])

	const applyBuild = useCallback(
		(target: CiTarget, next: CiBuildStatus | null) => {
			if (!mounted.current) return
			const wasActive = isCiBuildActive(lastStatus.current[target])
			lastStatus.current[target] = next?.status ?? null
			if (wasActive && next?.status === "Success") markCiBuildFinished()
			setBuilds((prev) => ({...prev, [target]: next}))
			setLoaded((prev) => (prev[target] ? prev : {...prev, [target]: true}))
		},
		[markCiBuildFinished],
	)

	/** Newest console build of one target. */
	const refreshTarget = useCallback(
		async (target: CiTarget) => {
			const seq = ++requestSeq.current[target]
			try {
				const next = await getCiBuildStatus(undefined, target)
				if (!mounted.current || seq !== requestSeq.current[target]) return
				setFor<string | null>(setPollErrors, target, null)
				// The server filters by target; anything else is ignored rather than shown on the wrong card.
				applyBuild(target, next && next.target === target ? next : null)
			} catch (error: any) {
				if (!mounted.current || seq !== requestSeq.current[target]) return
				setFor<string | null>(setPollErrors, target, error?.message || "Failed to fetch the build status")
				setLoaded((prev) => (prev[target] ? prev : {...prev, [target]: true}))
			}
		},
		[getCiBuildStatus, applyBuild],
	)

	/**
	 * One build by its SSM command id; lands on the card of the target it was started for.
	 * Resolves true when the build was put on a card.
	 */
	const refreshCommand = useCallback(
		async (commandId: string, expectedTarget?: CiTarget): Promise<boolean> => {
			const target0 = expectedTarget
			const seq0 = target0 ? ++requestSeq.current[target0] : 0
			try {
				const next = await getCiBuildStatus(commandId)
				if (!mounted.current) return false
				// With a commandId keep the last known state when the build is not found.
				if (!next) return false
				const target = isCiTarget(next.target) ? next.target : target0
				if (!target) return false
				if (target0 && target === target0 && seq0 !== requestSeq.current[target]) return false
				if (target !== target0) requestSeq.current[target]++
				setFor<string | null>(setPollErrors, target, null)
				applyBuild(target, next)
				return true
			} catch (error: any) {
				if (!mounted.current || !target0 || seq0 !== requestSeq.current[target0]) return false
				setFor<string | null>(setPollErrors, target0, error?.message || "Failed to fetch the build status")
				return false
			}
		},
		[getCiBuildStatus, applyBuild],
	)

	const refreshAll = useCallback(() => {
		TARGETS.forEach((t) => refreshTarget(t))
	}, [refreshTarget])

	// On mount: the latest console build of each target (e.g. after a page refresh).
	useEffect(() => {
		refreshAll()
	}, [refreshAll])

	const activeTargets = TARGETS.filter((t) => isCiBuildActive(builds[t]?.status))
	const anyActive = activeTargets.length > 0
	// Stable key of the running builds: polling restarts only when that set changes.
	const activeKey = activeTargets.map((t) => `${t}:${builds[t]?.commandId ?? ""}`).join("|")

	// Poll the running build(s).
	useEffect(() => {
		if (!activeKey) return
		const running = activeKey.split("|").map((entry) => {
			const [target, commandId] = entry.split(":")
			return {target: target as CiTarget, commandId}
		})
		const timer = setInterval(() => {
			running.forEach(({target, commandId}) =>
				commandId ? refreshCommand(commandId, target) : refreshTarget(target),
			)
		}, POLL_INTERVAL_MS)
		return () => clearInterval(timer)
	}, [activeKey, refreshCommand, refreshTarget])

	// Elapsed-time ticker while something runs.
	useEffect(() => {
		if (!anyActive) return
		setNow(Date.now())
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [anyActive])

	const openChooser = (target: CiTarget) => {
		setMessage(null)
		setBranch("master")
		setChooserTarget(target)
	}

	const handleStart = async () => {
		if (!chooserTarget) return
		const target = chooserTarget
		setStarting(true)
		setMessage(null)
		try {
			const result = await startCiBuild(target, branch)
			if (!mounted.current) return
			setChooserTarget(null)
			if (result.alreadyRunning) {
				setMessage({kind: "info", text: "A build is already running. Showing its status."})
				// Attach to the running build; if it cannot be fetched by id, reload both cards by target
				// (one of them then shows the running build and both Make buttons get disabled).
				const attached = result.commandId ? await refreshCommand(result.commandId) : false
				if (!attached && mounted.current) refreshAll()
			} else {
				requestSeq.current[target]++
				setFor<boolean>(setShowOutput, target, false)
				setFor<string | null>(setPollErrors, target, null)
				applyBuild(target, {
					commandId: result.commandId,
					target,
					branch,
					status: result.status || "Pending",
					requestedAt: new Date().toISOString(),
					finishedAt: null,
					outputTail: "",
				})
			}
		} catch (error: any) {
			if (mounted.current) setMessage({kind: "error", text: `Error: ${error?.message || "Failed to start the build"}`})
		} finally {
			if (mounted.current) setStarting(false)
		}
	}

	const makeDisabled = anyActive || starting

	return (
		<div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-6">
			<h3 className="font-semibold mb-1 text-gray-900 dark:text-gray-100">CI Build (RTL app processor)</h3>
			<p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
				Builds an OTA image on the CI host and stores it in S3. It does not start an OTA update - send
				it to a device from the RTL Software Upload section ("Choose file" &rarr; "From S3").
			</p>

			<div className="flex gap-3 items-center flex-wrap">
				{TARGETS.map((target) => (
					<button
						key={target}
						onClick={() => openChooser(target)}
						disabled={makeDisabled}
						title={anyActive ? "A build is running - wait until it finishes" : undefined}
						className={`px-4 py-2 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed ${
							target === "angel" ? "bg-blue-600 hover:bg-blue-700" : "bg-orange-500 hover:bg-orange-600"
						} ${chooserTarget === target ? "ring-2 ring-offset-1 ring-gray-400 dark:ring-offset-gray-800" : ""}`}>
						Make {TARGET_LABEL[target]}
					</button>
				))}
			</div>

			{chooserTarget && !anyActive && (
				<div className="mt-3 p-3 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 inline-flex flex-wrap items-center gap-3">
					<span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
						Make {TARGET_LABEL[chooserTarget]} from branch:
					</span>
					<div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden" role="radiogroup">
						{CI_BRANCHES.map((b) => (
							<button
								key={b}
								role="radio"
								aria-checked={branch === b}
								onClick={() => setBranch(b)}
								disabled={starting}
								className={`px-3 py-1 text-sm ${
									branch === b
										? "bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900"
										: "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
								}`}>
								{b}
							</button>
						))}
					</div>
					<button
						onClick={handleStart}
						disabled={starting}
						className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
						{starting ? "Starting..." : "Start"}
					</button>
					<button
						onClick={() => setChooserTarget(null)}
						disabled={starting}
						className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
						Cancel
					</button>
				</div>
			)}

			{message && (
				<div
					className={`mt-3 text-sm p-3 rounded border ${
						message.kind === "error"
							? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700"
							: "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700"
					}`}>
					{message.text}
				</div>
			)}

			<div className="mt-3 space-y-2">
				{TARGETS.map((target) => (
					<BuildCard
						key={target}
						target={target}
						build={builds[target]}
						loaded={loaded[target]}
						now={now}
						pollError={pollErrors[target]}
						showOutput={showOutput[target]}
						onToggleOutput={() => setShowOutput((prev) => ({...prev, [target]: !prev[target]}))}
					/>
				))}
			</div>
		</div>
	)
}

export default CiBuildSection
